import { useState, useRef, useCallback, useEffect } from 'react';
import { fetchProjectAIConfig, clearAIParametersCache } from '../../../utils/api';
// SSE接続を無効化（軽量化のため）
// import { useAIConfigSSE } from './useAIConfigSSE';

/**
 * AI設定管理フック
 * @param {string} projectId - プロジェクトID
 * @returns {object} AI設定とその管理関数
 */
export function useAIConfig(projectId) {

    const [aiConfig, setAiConfig] = useState(null);
    const [checkingAiConfig, setCheckingAiConfig] = useState(true);
    const [aiConfigError, setAiConfigError] = useState(null);
    const aiConfigFetchingRef = useRef(false);
    const previousModelTypeRef = useRef(null);

    const checkAiConfig = useCallback(async () => {
        if (!projectId || aiConfigFetchingRef.current) {
            return;
        }

        aiConfigFetchingRef.current = true;

        try {
            const config = await fetchProjectAIConfig(projectId);

            const oldModelType = previousModelTypeRef.current;
            const modelTypeChanged = oldModelType !== null && config?.model_type && oldModelType !== config.model_type;

            // モデルタイプ変更を検知した場合はログ出力（previousModelTypeRefを更新する前に）
            if (modelTypeChanged) {
                // パラメータキャッシュをクリアして最新の設定を反映
                clearAIParametersCache(projectId);
            }

            setAiConfig(config);

            // 初回読み込み時のみモデルタイプを記録
            if (previousModelTypeRef.current === null && config?.model_type) {
                previousModelTypeRef.current = config.model_type;
            } else if (modelTypeChanged) {
                // モデルタイプが変更された場合、openingEffect内で検知できるように、previousModelTypeRefは更新しない
                // openingEffect内でopeningメッセージを更新した後に更新する
            } else if (config?.model_type && previousModelTypeRef.current !== config.model_type) {
                // モデルタイプが変更されていないが、previousModelTypeRefが一致していない場合は更新
                // （通常は発生しないが、念のため）
                previousModelTypeRef.current = config.model_type;
            }

            // パラメータキャッシュをクリアして最新の設定を反映（初回読み込み時も）
            if (oldModelType === null) {
                clearAIParametersCache(projectId);
            }

            if (!config.is_active) {
                setAiConfigError({
                    type: 'ai_disabled',
                    message: 'このプロジェクトのAI機能は無効です。管理者に問い合わせてください。'
                });
            } else {
                setAiConfigError(null);
            }
        } catch (err) {

            const errorData = err?.response?.data;
            const errorCode = errorData?.error;

            // ネットワークエラー（接続拒否など）の処理
            if (!err?.response) {
                setAiConfigError({
                    type: 'network_error',
                    message: 'バックエンドサーバーに接続できません。サーバーが起動しているか確認してください。'
                });
            } else if (errorCode === 'ai_config_not_found') {
                setAiConfigError({
                    type: 'ai_config_not_found',
                    message: 'このプロジェクトにはAI設定がありません。管理者に問い合わせてください。'
                });
            } else if (errorCode === 'ai_disabled') {
                setAiConfigError({
                    type: 'ai_disabled',
                    message: 'このプロジェクトのAI機能は無効です。管理者に問い合わせてください。'
                });
            } else if (errorCode === 'api_key_not_configured') {
                setAiConfigError({
                    type: 'api_key_not_configured',
                    message: 'AI機能の設定が完了していません。管理者に問い合わせてください。'
                });
            } else {
                // その他のエラー（権限エラーなど）も表示
                setAiConfigError({
                    type: 'unknown',
                    message: 'AI設定の取得に失敗しました。管理者に問い合わせてください。'
                });
            }
        } finally {
            setCheckingAiConfig(false);
            aiConfigFetchingRef.current = false;
        }
    }, [projectId]);

    // checkAiConfigをuseRefに保存して、useEffectの依存配列から除外
    const checkAiConfigRef = useRef(checkAiConfig);
    checkAiConfigRef.current = checkAiConfig;

    // projectIdが変更されたときに自動的にcheckAiConfigを呼び出す
    useEffect(() => {
        if (projectId) {
            checkAiConfigRef.current();
        }
    }, [projectId]); // checkAiConfigを依存配列から除外

    // SSE通知を受けた時に設定を再取得し、パラメータキャッシュをクリア
    // SSE接続を無効化（軽量化のため）
    // const handleConfigChanged = useCallback(() => {
    //     // パラメータキャッシュをクリア
    //     clearAIParametersCache(projectId);
    //     // 設定を再取得
    //     checkAiConfigRef.current();
    // }, [projectId]);

    // SSE接続を確立（設定変更をリアルタイムで検知）
    // SSE接続を無効化（軽量化のため）
    // useAIConfigSSE(projectId, handleConfigChanged);

    return {
        aiConfig,
        checkingAiConfig,
        aiConfigError,
        checkAiConfig
    };
}

